import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, UserPlus, CheckCircle, Loader2, ShieldAlert, Lock, Share2 } from 'lucide-react';
import { Friendship } from '../types/friends';
import { Cruise } from '../context/CruiseContext';
import { useAuth } from '../context/AuthContext';
import { useFriends } from '../context/FriendContext';
import { useDisplayPreferences } from '../hooks/useDisplayPreferences';
import { getInvitedFriendIdsForCruise } from '../services/friendService';
import { formatFriendDisplayName, getFriendInitial } from '../utils/friendDisplayUtils';

interface AddFriendToCruiseModalProps {
  isOpen: boolean;
  onClose: () => void;
  cruise: Cruise;
  friends: Friendship[];
  existingCompanionIds?: string[];
  sharedWith?: string[];
  onShareCruise?: (friendId: string) => Promise<void>;
  onInvite: (friendId: string) => Promise<void>;
}

const AddFriendToCruiseModal = ({
  isOpen,
  onClose,
  cruise,
  friends,
  existingCompanionIds = [],
  sharedWith,
  onShareCruise,
  onInvite,
}: AddFriendToCruiseModalProps) => {
  const { currentUser } = useAuth();
  const { shareCruiseWithFriend, getCruiseSharedWith } = useFriends();
  const { formatDate } = useDisplayPreferences();
  const [searchTerm, setSearchTerm] = useState('');
  const [invitingId, setInvitingId] = useState<string | null>(null);
  const [invitedIds, setInvitedIds] = useState<string[]>(() => cruise.invitedFriendIds || []);
  const [localSharedWith, setLocalSharedWith] = useState<string[]>(() => sharedWith || []);
  const [restrictedPromptFriend, setRestrictedPromptFriend] = useState<Friendship | null>(null);
  const [isSharingAndInviting, setIsSharingAndInviting] = useState(false);

  // Keep localSharedWith in sync with prop
  useEffect(() => {
    if (sharedWith) {
      setLocalSharedWith(sharedWith);
    }
  }, [sharedWith]);

  // If sharedWith wasn't passed as prop, fetch it as fallback
  useEffect(() => {
    if (!sharedWith && cruise.id && currentUser) {
      getCruiseSharedWith(cruise.id)
        .then((ids) => setLocalSharedWith(ids))
        .catch((err) => console.error('Failed to get sharedWith for cruise:', err));
    }
  }, [sharedWith, cruise.id, currentUser, getCruiseSharedWith]);

  // Sync invitedIds whenever cruise.invitedFriendIds changes
  useEffect(() => {
    if (cruise.invitedFriendIds && cruise.invitedFriendIds.length > 0) {
      setInvitedIds((prev) => Array.from(new Set([...prev, ...cruise.invitedFriendIds!])));
    }
  }, [cruise.invitedFriendIds]);

  // Query Firestore notifications to ensure any previously sent invites are detected
  useEffect(() => {
    if (!isOpen || !currentUser || !cruise.id || friends.length === 0) return;

    let isMounted = true;
    const checkExistingInvites = async () => {
      try {
        const friendIds = friends.map((f) => f.friendId);
        const remoteInvitedIds = await getInvitedFriendIdsForCruise(
          currentUser.uid,
          cruise.id,
          friendIds
        );
        if (isMounted && remoteInvitedIds.length > 0) {
          setInvitedIds((prev) => Array.from(new Set([...prev, ...remoteInvitedIds])));
        }
      } catch (err) {
        console.error('Failed to check existing invites for cruise:', err);
      }
    };

    checkExistingInvites();

    return () => {
      isMounted = false;
    };
  }, [isOpen, currentUser, cruise.id, friends]);

  if (!isOpen) return null;

  const filteredFriends = friends.filter((f) => {
    const name = formatFriendDisplayName(f);
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleSendInvite = async (friendId: string) => {
    try {
      setInvitingId(friendId);
      await onInvite(friendId);
      setInvitedIds((prev) => Array.from(new Set([...prev, friendId])));
    } catch (error) {
      console.error('Error inviting friend:', error);
    } finally {
      setInvitingId(null);
    }
  };

  const handleInviteClick = (friend: Friendship) => {
    // Check if friend has restricted access and cruise has NOT been shared with them
    if (friend.accessLevel === 'restricted-access' && !localSharedWith.includes(friend.friendId)) {
      setRestrictedPromptFriend(friend);
      return;
    }

    // Full access or already shared, proceed directly
    handleSendInvite(friend.friendId);
  };

  const handleConfirmShareAndInvite = async () => {
    if (!restrictedPromptFriend) return;
    const friendId = restrictedPromptFriend.friendId;

    try {
      setIsSharingAndInviting(true);
      setInvitingId(friendId);

      // 1. Share the cruise with the friend
      if (onShareCruise) {
        await onShareCruise(friendId);
      } else {
        await shareCruiseWithFriend(cruise.id, friendId);
      }
      setLocalSharedWith((prev) => Array.from(new Set([...prev, friendId])));

      // 2. Send the invitation
      await onInvite(friendId);
      setInvitedIds((prev) => Array.from(new Set([...prev, friendId])));

      // Close modal
      setRestrictedPromptFriend(null);
    } catch (error) {
      console.error('Error sharing and inviting friend:', error);
    } finally {
      setIsSharingAndInviting(false);
      setInvitingId(null);
    }
  };

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Tag Friend on Cruise</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {cruise.shipName} • {formatDate(cruise.departureDate)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Search bar */}
          <div className="p-4 border-b border-gray-100 bg-gray-50">
            <div className="relative">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search friends..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-secondary-500"
              />
            </div>
          </div>

          {/* Friends List */}
          <div className="overflow-y-auto p-4 flex-1 space-y-3">
            {friends.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="font-medium text-gray-700">No friends added yet</p>
                <p className="text-xs text-gray-500 mt-1">
                  Add friends in the Friends tab to tag them on your cruises!
                </p>
              </div>
            ) : filteredFriends.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                No friends match "{searchTerm}"
              </div>
            ) : (
              filteredFriends.map((friend) => {
                const displayName = formatFriendDisplayName(friend);
                const isAlreadyCompanion = existingCompanionIds.includes(friend.friendId);
                const hasBeenInvited = invitedIds.includes(friend.friendId);
                const isProcessing = invitingId === friend.friendId;
                const isRestricted = friend.accessLevel === 'restricted-access';
                const isShared = localSharedWith.includes(friend.friendId);

                return (
                  <div
                    key={friend.friendId}
                    className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:border-teal-200 transition-colors gap-3"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="h-10 w-10 rounded-full bg-secondary-600 flex items-center justify-center text-white font-semibold shrink-0">
                        {getFriendInitial(friend)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 truncate text-sm">{displayName}</p>
                        <div className="flex items-center gap-2 mt-0.5 min-w-0">
                          <span className="text-xs text-gray-500 truncate min-w-0">{friend.friendEmail}</span>
                          {isRestricted && (
                            <span
                              className={`shrink-0 whitespace-nowrap text-[10px] font-medium px-1.5 py-0.5 rounded border leading-tight ${
                                isShared
                                  ? 'bg-teal-50 text-teal-700 border-teal-200'
                                  : 'bg-amber-50 text-amber-700 border-amber-200'
                              }`}
                            >
                              {isShared ? 'Shared' : 'Restricted'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="shrink-0">
                      {isAlreadyCompanion ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-teal-600 bg-teal-50 px-2.5 py-1 rounded-full border border-teal-200">
                          <CheckCircle className="h-3.5 w-3.5" />
                          Sailing
                        </span>
                      ) : hasBeenInvited ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2.5 py-1 rounded-full border border-green-200">
                          <CheckCircle className="h-3.5 w-3.5" />
                          Invited
                        </span>
                      ) : (
                        <button
                          onClick={() => handleInviteClick(friend)}
                          disabled={isProcessing}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-secondary-600 hover:bg-secondary-500 text-white rounded-md text-xs font-medium transition-colors disabled:opacity-50"
                        >
                          {isProcessing ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <>
                              <UserPlus className="h-3.5 w-3.5" />
                              Invite
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-100 transition-colors font-medium"
            >
              Done
            </button>
          </div>
        </motion.div>
      </div>

      {/* Restricted Friend Access Required Confirmation Modal */}
      {restrictedPromptFriend && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-200 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-amber-100 bg-gradient-to-r from-amber-50 to-orange-50">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center text-amber-700 shrink-0">
                  <ShieldAlert className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Cruise Access Required
                  </h3>
                  <p className="text-xs text-amber-800">
                    Restricted Friend Permissions
                  </p>
                </div>
              </div>
              <button
                onClick={() => setRestrictedPromptFriend(null)}
                disabled={isSharingAndInviting}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-700 leading-relaxed">
                <strong className="text-gray-900 font-semibold">{formatFriendDisplayName(restrictedPromptFriend)}</strong> has <span className="font-semibold text-amber-800">Restricted Access</span> to your cruise calendar and cannot currently see this sailing.
              </p>

              <div className="bg-amber-50/80 border border-amber-200/80 rounded-lg p-3.5 space-y-1.5 text-xs text-amber-900">
                <p className="font-semibold flex items-center gap-1.5 text-amber-950">
                  <Lock className="h-3.5 w-3.5 text-amber-700 shrink-0" />
                  Why is this needed?
                </p>
                <p className="leading-normal">
                  To tag friends and send them an itinerary invite, you must share this cruise so they have permission to view it.
                </p>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center justify-between text-xs">
                <div>
                  <p className="font-medium text-gray-900">{cruise.shipName}</p>
                  <p className="text-gray-500">{formatDate(cruise.departureDate)}</p>
                </div>
                <span className="text-xs px-2 py-0.5 rounded font-medium bg-amber-100 text-amber-800 border border-amber-200">
                  Not Shared
                </span>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex gap-3">
              <button
                type="button"
                onClick={() => setRestrictedPromptFriend(null)}
                disabled={isSharingAndInviting}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmShareAndInvite}
                disabled={isSharingAndInviting}
                className="flex-1 px-4 py-2 bg-secondary-600 hover:bg-secondary-500 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {isSharingAndInviting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Share2 className="h-4 w-4" />
                    Share & Invite
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default AddFriendToCruiseModal;
